import type {
  Definitie,
  Instantie,
  Side,
  Laag,
  Projection,
  ProjectionSection,
  Permission,
  Role,
  Section,
  SectionType,
  Field,
} from '@eformulieren/model'
import { Toekenning, fields, heeftToetslaag, isVisibilityRule, visibilitySources, type Verwijzingen } from '@eformulieren/model'
import type { State } from './state.js'
import { referenceValues } from './verwijzing.js'
import { determineVisibility } from './visibility.js'

/**
 * De projectieberekening: wat deze persoon van dit formulier krijgt (R-01a,
 * ontwerp 5.2).
 *
 * Vier ingrediënten (5.2, B18): de bevroren definitie van de publicatie, de
 * toekenningen van deze persoon op dit dossier en bouwblok met de deling als een van
 * hun bronnen, de laag die aan zet is, en de kant uit de ingang. Wie geen recht op een
 * sectie heeft, krijgt die sectie niet: ze staat er niet in, en dat is geen fout maar
 * de projectie zelf (R-68, ontwerp 9.2 regel 1).
 *
 * **Dit is de enige implementatie.** Elk pad dat leest of schrijft gebruikt haar:
 * het projectie-endpoint, elke antwoordopslag, het indienen en elke overdracht.
 * Een tweede kopie voor een ander kanaal is een fout en geen optimalisatie; in de
 * PortaalPOC stond dezelfde regel drie keer los en liepen het gewone verzoek en
 * de live verbinding uiteen (ontwerp 5.2, bijlage B probleem 3).
 *
 * **Er is sinds opdracht 4b van de reeks toegang via toekenningen één ingang**,
 * `computeProjectionFromToekenningen`. De berekening van vóór het register, met de eigenaar
 * op de instantie, de rol uit het token en het `sharing`-record op e-mailadres
 * (`berekenProjectie`, `isLaagEigenaar`, `delingRecht`), is weg: de pod leest het register,
 * en een pod van vóór de reeks draait met de engine van zijn eigen publicatiemoment (D-36).
 *
 * **Eén keuze van deze functie die geen eis heeft gemaakt: de invuller krijgt geen recht
 * op een toetssectie.** R-75 laat een teruglegopmerking naar de indiener teruggaan, maar
 * de route daarheen is een laag-entry (4.7) en niet per se een geprojecteerde toetssectie.
 * Dat is de kant die niets prijsgeeft.
 *
 * **De spiegel daarvan is geen keuze van deze functie, want R-193 heeft haar al
 * gemaakt.** De vraag of de toetser een invoersectie mag lezen staat ernaast, maar
 * hoort er niet bij: R-193 luidt letterlijk "In de toetsbeurt zijn de invoervelden
 * alleen-lezen en zijn alleen de toetsvelden bewerkbaar", en bindt dat leesrecht dus aan
 * de toetsbeurt. Wat de regel is staat bij `rolRecht`.
 */

/** Geen recht is een echte uitkomst en niet een ontbrekende waarde. */
type Access = Permission | 'geen'

const STRENGTH: Record<Access, number> = { geen: 0, lezen: 1, bewerken: 2 }

function strongest(a: Access, b: Access): Access {
  return STRENGTH[a] >= STRENGTH[b] ? a : b
}

function atMost(permission: Access, boundary: Access): Access {
  return STRENGTH[permission] <= STRENGTH[boundary] ? permission : boundary
}

/**
 * In welke laag een sectie schrijft (D-04, D-56, R-172).
 *
 * Een bronsectie hoort bij geen laag: ze is inhoud uit het dossier en wordt door
 * niemand geschreven (R-56).
 *
 * Geëxporteerd omdat de pod dezelfde vraag stelt en op een ander moment: welk
 * sectiedocument hoort bij welke sectie, want de laag zit in de documentnaam
 * (ontwerp 6.8, 9.2). Die afbeelding twee keer opschrijven is precies de tweede
 * kopie die 5.2 verbiedt; dan kan de projectie een sectie in de invoerlaag zetten
 * terwijl de pod haar document in de toetslaag zoekt.
 */
export function laagOf(type: SectionType): 'invoer' | 'toets' | null {
  if (type === 'toets') return 'toets'
  if (type === 'gewoon') return 'invoer'
  return null
}

/**
 * In welke laag deze rol schrijft (R-172b).
 *
 * De rol wijst de laag aan, en niet omgekeerd: een invuller schrijft in de invoerlaag, een
 * toetser in de toetslaag. Geëxporteerd om dezelfde reden als `laagOf`: de pod stelt bij een
 * overdracht dezelfde vraag (wie sluit deze beurt af, R-69), en dat is één regel die op één
 * plek hoort te staan.
 */
export function laagForRole(role: Role): 'invoer' | 'toets' {
  return role === 'toetser' ? 'toets' : 'invoer'
}

/**
 * In welke laag deze kijker schrijft, volgens zijn projectie (R-172b, R-138).
 *
 * Meestal de laag van zijn rol, en dat is `laagForRole`. De uitzondering is wie als invuller én
 * als toetser is toegekend, zoals een NWO-medewerker die zelf invult en ook toetst: de projectie
 * draagt dan de rol `toetser`, en `laagForRole` alleen zou hem in de invoerbeurt buiten zijn eigen
 * invoerlaag zetten. Hij schrijft in de laag die aan zet is, want beide lagen zijn van hem.
 *
 * Een vraag over de weergave en niet over het recht: wat iemand mag schrijven staat per sectie in
 * de projectie en de pod toetst elke schrijfactie zelf. De pagina (`apps/runtime/pagina/gedaante.ts`)
 * en de renderer vragen dit om de gedaante te kiezen en om te weten welke secties de kijker zelf
 * afrondt; één regel op één plek, want twee afleidingen lopen uiteen.
 */
export function ownLaag(projection: Pick<Projection, 'rol' | 'magIndienen' | 'actieveLaag'>): Laag {
  if (projection.rol === 'toetser' && projection.magIndienen) return projection.actieveLaag
  return laagForRole(projection.rol)
}

/**
 * Bezit wie deze projectie heeft de laag waarin hij wil handelen (R-69, R-172b)?
 *
 * De invoerlaag is van wie als invuller van het processysteem is toegekend, en dat is precies
 * `magIndienen`; de toetslaag van wie als toetser is toegekend, en dat is de rol `toetser` in de
 * projectie. Beide komen alleen uit een toekenning van het processysteem: niet uit een deling en
 * niet uit een opening door beheer (`computeProjectionFromToekenningen`). Wie beide toekenningen
 * heeft, bezit beide lagen.
 *
 * Hier en niet in de pod, want drie plekken stellen dezelfde vraag: de overdracht en het oordeel in
 * de pod (`handover.ts`, `verdict.ts`), de bewaking van het akkoordveld (`consentGuard.ts`, besluit
 * van 3 november 2026: alleen wie de laag bezit geeft of wist het akkoord), en de renderer, die het
 * akkoord voor ieder ander alleen-lezen toont. Twee lezingen van één regel lopen uiteen.
 */
export function ownsLaag(projection: Pick<Projection, 'rol' | 'magIndienen'>, laag: Laag): boolean {
  return laag === 'invoer' ? projection.magIndienen : projection.rol === 'toetser'
}

/**
 * Wat iemand op grond van zijn rol op deze sectie mag.
 *
 * Voor een toekenning van het processysteem met deze rol: haar rol bezit de laag waarop ze
 * wijst (R-172b). Wie die toekenning niet heeft komt hier niet langs.
 *
 * Bewerken vraagt drie dingen tegelijk: de rol bezit de laag van de sectie
 * (R-172, R-172b), die laag is aan zet (R-193), en de instantie is niet bevroren
 * (U21, R-86). Valt een van de drie weg, dan blijft er lezen over waar er iets te
 * lezen is.
 *
 * **De toetser leest de invoerlaag vanaf de toetsbeurt, en niet eerder.** Dat is de eis
 * zelf en geen aanvullende keuze: R-193 zegt "In de toetsbeurt zijn de invoervelden
 * alleen-lezen en zijn alleen de toetsvelden bewerkbaar", dus het leesrecht van de
 * toetser op de invoer hangt aan die beurt. Staat het formulier open terwijl de
 * invoerlaag aan zet is, dan geeft de andere laag hem `geen`; in elke andere stand houdt
 * hij `lezen`, want dan is de toetsbeurt er of geweest en is nalezen van wat hij zelf
 * heeft getoetst een beschreven weergave (R-86, R-98). De aanvulinstantie na een
 * terugleggen valt onder diezelfde regel en niet naast haar: `beurtNa('teruggelegd')`
 * levert `invoer` en `open`, precies de stand van de eerste beurt, dus ook daar leest de
 * toetser niet mee zolang de invuller aanvult. Bevroren met `actieveLaag: 'invoer'`, de
 * ingediende voorganger, is juist de stand waarin hij wél leest.
 *
 * **De oude vorm was ruimer dan de eis die hij aanhaalde.** Zij gaf de toetser
 * onvoorwaardelijk leesrecht op elke sectie van de andere laag, zonder enige
 * beurtvoorwaarde, terwijl één regel lager wél op de beurt werd gekeken. Via `permissionOn` in
 * de pod (`apps/runtime/src/samenwerken.ts`) reisde datzelfde sectierecht mee naar de
 * samenwerkverbinding, dus las een toetser live mee in een aanvraag die op dat moment werd
 * geschreven. Dat is geen weergave van wat er te toetsen valt maar meelezen terwijl iemand
 * zit te typen; het rechtzetten is daarmee een correctie en geen beleidswijziging.
 */
export function rolePermission(type: SectionType, role: Role, instantie: Instantie): Access {
  const laag = laagOf(type)
  // Een bronsectie is alleen-lezen voor iedereen die het formulier mag zien.
  if (laag === null) return 'lezen'
  /** Of er op dit moment in de laag van deze sectie wordt geschreven (R-193, U21, R-86). */
  const hasTurn = instantie.actieveLaag === laag && instantie.status === 'open'
  const ownLaag = laagForRole(role)
  if (laag !== ownLaag) {
    // De invuller krijgt de toetslaag niet; zie de kop van dit bestand.
    if (role !== 'toetser') return 'geen'
    // De toetser leest de invoer waarover hij een uitspraak doet, maar pas vanaf zijn
    // eigen beurt (R-193). Zolang de invoerlaag zelf aan zet is, is er nog niets
    // overgedragen en geeft zij hem niets.
    return hasTurn ? 'geen' : 'lezen'
  }
  return hasTurn ? 'bewerken' : 'lezen'
}

/**
 * De drie grenzen op wat een deling op een sectie geeft, los van waar het recht van de deler
 * vandaan komt (R-65a, R-65b, R-193).
 *
 * Het recht van de deler rekent `computeProjectionFromToekenningen` uit de toekenningen die hij op
 * dit moment heeft. Wat daarna gebeurt staat hier: nooit meer dan de deler heeft, op een
 * toetssectie nooit meer dan lezen, en nooit meer dan de beurt toestaat.
 */
function limitDeling(section: Section, given: Permission, fromSharer: Access, instantie: Instantie): Access {
  let out = atMost(given, fromSharer)

  if (section.type === 'toets') out = atMost(out, 'lezen')

  const laag = laagOf(section.type)
  const hasTurn = laag !== null && instantie.actieveLaag === laag && instantie.status === 'open'
  if (!hasTurn) out = atMost(out, 'lezen')

  return out
}

/**
 * Van het recht per sectie naar de secties die de client krijgt: de vormen kiezen (R-01a,
 * R-01d) en elke sectie tot haar meereizende velden en regels snijden.
 *
 * Los van de berekening van het recht, want hoe een recht per sectie een projectie wordt hangt
 * niet af van waar dat recht vandaan komt.
 */
function projectSections(
  definitie: Definitie,
  access: Map<string, Permission>,
  state: State,
  verwijzingen: Verwijzingen,
): ProjectionSection[] {
  const chosen = chooseShapes(definitie, access, state, verwijzingen)

  const sections: ProjectionSection[] = []
  // De plaats in de definitie, apart geteld voor toetssecties en de rest (`ProjectionSection.nummer`).
  let reviewPlace = 0
  let inputPlace = 0
  for (const section of definitie.secties) {
    const place = section.type === 'toets' ? ++reviewPlace : ++inputPlace
    const permission = access.get(section.id)
    if (permission === undefined || !chosen.secties.has(section.id)) continue
    sections.push({
      ...section,
      recht: permission,
      nummer: place,
      velden: intersectFields(section.velden, chosen.velden),
      regels: section.regels.filter((rule) => chosen.regels.has(rule.id)),
    })
  }
  return sections
}

/** Wat de projectie uit toekenningen nodig heeft over wat deze persoon hier is toegekend (R-155b). */
export interface Toekenningen {
  /**
   * De levende toekenningen van deze persoon op dit dossier voor dit bouwblok, uit alle drie de
   * bronnen. De opslagdienst levert alleen levende; de berekening weegt ze toch zelf.
   */
  readonly eigen: readonly Toekenning[]
  /**
   * Per deler van een eigen deling diens levende toekenningen op hetzelfde dossier en blok.
   * Daaruit volgt wat de deler op dit moment zelf heeft (R-65a). Een deler die hier ontbreekt
   * heeft niets, en zijn deling geeft dan ook niets.
   */
  readonly delers: Readonly<Record<string, readonly Toekenning[]>>
}

export interface ProjectionFromToekenningenInput {
  /** De bevroren definitie van de publicatie (R-99). Haar blok is het blok van de publicatie. */
  readonly definitie: Definitie
  readonly publicatieId: string
  readonly instantie: Instantie
  /**
   * De stand van de lopende beurt.
   *
   * Nodig omdat een zichtbaarheidsconditie waarvan het bronveld buiten de projectie valt niet
   * mee mag reizen: dan blijft de regel op de server en gaat alleen de uitkomst mee (R-01d,
   * ontwerp 5.2). Die uitkomst is niet te bepalen zonder de antwoorden.
   */
  readonly stand: State
  /** De vaste sleutel van wie het verzoek doet, met zijn ingang ervoor (R-155e). */
  readonly persoon: string
  /** Intern of extern, uit de ingang waarlangs hij inlogde (R-155a). */
  readonly kant: Side
  readonly toegang: Toekenningen
}

/**
 * De projectie uit toekenningen: wat deze persoon van dit formulier krijgt, uit wat hem in het
 * register is toegekend (R-155, R-155b, ontwerp 5.2, B18).
 *
 * Drie bronnen, elk met een eigen regel, en per sectie het sterkste van de drie:
 *
 * 1. **Het processysteem** wijst de laag aan (R-172b). Een toekenning als invuller bezit de
 *    invoerlaag, een toekenning als toetser de toetslaag, en dat kunnen per laag meer personen
 *    zijn (R-172). Per toekenning geeft `rolRecht` het recht met haar rol als laag-eigenaar; wie
 *    er twee heeft krijgt per sectie het sterkste.
 * 2. **Een deling** geeft het recht per sectie dat erop staat, met de grenzen van `limitDeling`,
 *    en nooit meer dan de deler op dit moment zelf heeft (R-65a). Wat de deler heeft volgt uit
 *    regel 1 over zijn eigen toekenningen in `toegang.delers`, en alleen uit die: een deler die
 *    zelf via een deling of door beheer binnenkwam geeft niets (R-65d), en een deler zonder
 *    levende toekenning ook niet (R-65e, als vangnet naast het vervallen in de opslagdienst).
 * 3. **Beheer** leest elke sectie, ook de toetslaag, in elke beurt, en schrijft nergens (R-155f).
 *
 * De rol op een toekenning uit een deling of van beheer wijst geen laag aan. Telde hij wel, dan
 * kreeg een medeschrijver met een deling op één sectie de hele invoerlaag.
 *
 * **Indienen en delen zijn eigenschappen van wie iemand is, en geen berekening over secties.**
 * Indienen kan wie als invuller van het processysteem is toegekend (R-69), delen wie een toekenning
 * van het processysteem heeft en aan de externe kant als invuller (R-64d, R-65d). Geen van beide
 * komt uit een deling of uit beheer.
 *
 * **De invoer wordt niet blind vertrouwd.** De opslagdienst levert alleen levende toekenningen van
 * deze persoon op dit dossier en dit blok, maar de berekening laat zelf weg wat daar niet aan
 * voldoet: een toekenning die het schema van het register zou weigeren, een ander dossier, een
 * blok dat niet het blok van de publicatie is, een andere persoon, een andere kant dan de ingang,
 * een vervallen of ingetrokken toekenning, een deling die niet binnengekomen is en een deling op een
 * andere instantie. Eén ding kan
 * ze niet nagaan: of een opening door beheer haar einde voorbij is. Daarvoor is een klok nodig,
 * en de engine rekent zonder klok; dat filter ligt bij de opslagdienst.
 *
 * **Een deling geldt alleen op de instantie waarop is gedeeld** (besluit 1 november 2026, R-155b).
 * Een toekenning van het processysteem geldt voor elke instantie van haar bouwblokken op het dossier
 * (R-155c); een deling niet. Na een overdracht die een opvolger in de lijn maakt (R-192), zoals
 * terugleggen, ziet een medeschrijver of meelezer de nieuwe instantie pas als de aanvrager opnieuw
 * deelt. Op de instantie waarop hij deelde blijft de deling gelden, ook als die bevroren is.
 */
export function computeProjectionFromToekenningen(input: ProjectionFromToekenningenInput): Projection {
  const { definitie, instantie, persoon: person, kant: side, stand: state } = input
  const applies = appliesHere(definitie, instantie)

  const own = input.toegang.eigen.filter((t) => applies(t) && t.persoon === person && t.kant === side)
  const processysteem = own.filter((t) => t.bron === 'processysteem')
  const delingen = own.filter((t) => t.bron === 'deling')
  const admin = own.some((t) => t.bron === 'beheer')

  /** Wat elke deler op dit moment zelf heeft: alleen zijn toekenningen van het processysteem. */
  const fromSharers = new Map<string, readonly Toekenning[]>()
  for (const deling of delingen) {
    const sharer = deling.deler
    if (sharer === null || fromSharers.has(sharer)) continue
    const theirs = Object.hasOwn(input.toegang.delers, sharer) ? (input.toegang.delers[sharer] ?? []) : []
    fromSharers.set(
      sharer,
      theirs.filter((t) => applies(t) && t.persoon === sharer && t.bron === 'processysteem'),
    )
  }

  /** Per sectie het sterkste recht uit processysteem en delingen; beheer komt er hierna bij. */
  const withoutAdmin = new Map<string, Permission>()
  for (const section of definitie.secties) {
    let permission = permissionFromProcessysteem(section.type, processysteem, instantie)
    for (const deling of delingen) {
      const given = deling.rechten?.[section.id]
      if (given === undefined || deling.deler === null) continue
      const fromSharer = permissionFromProcessysteem(section.type, fromSharers.get(deling.deler) ?? [], instantie)
      permission = strongest(permission, limitDeling(section, given, fromSharer, instantie))
    }
    if (permission !== 'geen') withoutAdmin.set(section.id, permission)
  }

  // Beheer leest alles en schrijft niets (R-155f), dus het voegt alleen lezen toe waar er nog
  // niets was.
  const access = new Map(withoutAdmin)
  if (admin) {
    for (const section of definitie.secties) if (!access.has(section.id)) access.set(section.id, 'lezen')
  }

  // De verwijzingen over de hele definitie: een voorwaarde op een verwijzing die deze lezer niet ziet,
  // rekent de pod hier uit en alleen de uitkomst gaat mee (12.31, ontwerp 5.2).
  const sections = projectSections(definitie, access, state, referenceValues(definitie, instantie))
  const respondent = processysteem.find((t) => t.rol === 'invuller')

  return {
    instantieId: instantie.id,
    publicatieId: input.publicatieId,
    definitieId: definitie.id,
    titel: definitie.titel,
    // Alleen als hij uit staat: een definitie zonder de instelling nummert (R-04).
    ...(definitie.nummering === false ? { nummering: false } : {}),
    actieveLaag: instantie.actieveLaag,
    // Voor de bevestiging na indienen: de invuller ziet de toetssectie niet, dus de renderer kan het
    // niet uit zijn eigen secties halen (besluit van 30 oktober 2026).
    getoetst: heeftToetslaag(definitie),
    kant: side,
    rol: processysteem.some((t) => t.rol === 'toetser') ? 'toetser' : 'invuller',
    magDelen: canShare(
      processysteem.some((t) => side === 'intern' || t.rol === 'invuller'),
      sections.length,
    ),
    magIndienen: respondent !== undefined,
    // Het register houdt één toekenning per persoon en rol op een dossier (`ProcessysteemList`),
    // dus er is hoogstens één invullertoekenning om `namens` uit te lezen.
    namens: respondent?.namens ?? null,
    beheer: admin && withoutAdmin.size === 0,
    secties: sections,
  }
}

/**
 * Of een toekenning hier telt: geldig volgens het register, voor dit dossier en het blok van
 * deze publicatie, bij een deling ook voor deze instantie, en nog levend (R-155b, R-155c, R-155d,
 * R-64c, R-66, R-65e, besluit 1 november 2026).
 *
 * Het blok komt uit de definitie: een publicatie hoort bij precies één bouwblok en haar schema
 * eist dat dat het blok van haar definitie is (ontwerp 3.1, `Publicatie`). Het register draagt bij
 * een deling altijd de instantie waarop is gedeeld (`Toekenning`), dus dit filter laat geen deling
 * zonder instantie door.
 */
function appliesHere(definitie: Definitie, instantie: Instantie): (toekenning: Toekenning) => boolean {
  return (toekenning) => {
    if (!Toekenning.safeParse(toekenning).success) return false
    if (toekenning.dossierId !== instantie.dossierId) return false
    if (!toekenning.blokken.includes(definitie.blok)) return false
    if (toekenning.vervallenOp !== null || toekenning.ingetrokkenOp !== null) return false
    if (toekenning.bron === 'deling' && toekenning.toestand !== 'binnengekomen') return false
    if (toekenning.bron === 'deling' && toekenning.instantieId !== instantie.id) return false
    return true
  }
}

/** Het sterkste rolrecht over toekenningen van het processysteem, elk als eigenaar van haar laag (R-172b). */
function permissionFromProcessysteem(type: SectionType, toekenningen: readonly Toekenning[], instantie: Instantie): Access {
  let permission: Access = 'geen'
  for (const toekenning of toekenningen) permission = strongest(permission, rolePermission(type, toekenning.rol, instantie))
  return permission
}

/**
 * Of deze persoon op deze instantie mag delen (R-64, R-64d, R-65d).
 *
 * Aan de interne kant mag wie een toekenning van het processysteem heeft delen op elk
 * formulier dat hij mag zien (R-64d). Aan de externe kant mag wie als invuller van het
 * processysteem is toegekend het: de aanvrager en zijn gemachtigden (R-64, R-65d), en dat
 * sluit uit dat een uitgenodigde medeschrijver op zijn beurt weer uitnodigt.
 * Doorgeven zou een keten opleveren waarin niemand nog kan narekenen hoeveel
 * recht er onderweg is bijgekomen, en R-65a is juist bedoeld om dat af te toppen.
 *
 * Die regel heeft sinds 3 september een eigen nummer: R-65d zegt dat het recht om te
 * delen niet uit een deling komt. Dat is met opzet een eigenschap van wie iemand is en
 * geen berekening over een deelboom, want zo'n afleiding moet bij elke doorverdeling
 * opnieuw kloppen. Wat deze functie niet doet is afdwingen: ze zegt wie mag delen; de
 * deelroute van de pod hoort zelf ook te weigeren (ontwerp 4.8, 9.2).
 */
function canShare(allowed: boolean, sectionCount: number): boolean {
  if (sectionCount === 0) return false
  return allowed
}

/**
 * Welke secties, velden en regels de projectie in gaan.
 *
 * De hoofdregel: een regel reist alleen mee als al zijn invoer ook meereist
 * (R-01a, R-01d, ontwerp 5.2). Voor een zichtbaarheidsconditie waarvan het
 * bronveld buiten de projectie valt betekent dat: de regel blijft op de server en
 * alleen de uitkomst gaat mee, dus het doel staat in de projectie of het staat er
 * niet.
 *
 * Heeft een doel meer dan één conditie en is er één niet projecteerbaar, dan gaat
 * de uitkomst van álle condities op dat doel mee en reist er geen enkele mee. De
 * helft laten meereizen zou de andere helft dubbel toepassen.
 *
 * De berekening loopt tot ze stabiel is en wordt alleen krapper: een weggevallen
 * veld kan een regel niet-projecteerbaar maken, en dat kan weer een volgend doel
 * laten vallen. Twee opruimregels horen erbij, want het schema laat ze niet weg:
 * een herhaalgroep zonder rijvelden en een sectie zonder velden bestaan niet.
 */
function chooseShapes(
  definitie: Definitie,
  access: Map<string, Permission>,
  state: State,
  verwijzingen: Verwijzingen,
): { secties: Set<string>; velden: Set<string>; regels: Set<string> } {
  // De serverwaarheid over de hele definitie: de pod ziet alles, ook wat deze
  // persoon niet mag zien, en ook de waarden van elke verwijzing (12.31).
  const real = determineVisibility(definitie.secties, state, verwijzingen)

  let sections = new Set([...access.keys()])
  let fieldsIn = fieldsOf(definitie, sections)
  let rules = new Set<string>()

  for (let round = 0; round <= definitie.secties.length + 1; round += 1) {
    const previousSections = sections.size
    const previousFields = fieldsIn.size

    /** Per doel de condities die erop staan, binnen de secties die we nog hebben. */
    const conditions = new Map<string, { id: string; projecteerbaar: boolean }[]>()
    const remainingRules = new Set<string>()

    for (const section of definitie.secties) {
      if (!sections.has(section.id)) continue
      for (const rule of section.regels) {
        if (isVisibilityRule(rule)) {
          const targetIn = sections.has(rule.doel) || fieldsIn.has(rule.doel)
          const projectable = targetIn && visibilitySources(rule).every((source) => fieldsIn.has(source))
          const list = conditions.get(rule.doel) ?? []
          list.push({ id: rule.id, projecteerbaar: projectable })
          conditions.set(rule.doel, list)
          continue
        }
        const target = 'doelSectie' in rule ? rule.doelSectie : rule.doelVeld
        if (sections.has(target) || fieldsIn.has(target)) remainingRules.add(rule.id)
      }
    }

    const baked = new Set<string>()
    for (const [target, list] of conditions) {
      if (list.every((c) => c.projecteerbaar)) {
        for (const c of list) remainingRules.add(c.id)
        continue
      }
      // De uitkomst gaat mee in plaats van de regel: het doel blijft alleen als de
      // server het nu zichtbaar vindt.
      if (!real.secties.has(target) && !real.velden.has(target)) baked.add(target)
    }

    sections = new Set([...sections].filter((id) => !baked.has(id)))
    fieldsIn = new Set([...fieldsOf(definitie, sections)].filter((id) => !baked.has(id)))
    cleanUp(definitie, sections, fieldsIn)
    rules = remainingRules

    if (sections.size === previousSections && fieldsIn.size === previousFields) break
  }

  // Laatste slag: een regel die zijn doel of bronveld onderweg kwijtraakte gaat niet mee.
  const out = new Set<string>()
  for (const section of definitie.secties) {
    if (!sections.has(section.id)) continue
    for (const rule of section.regels) {
      if (!rules.has(rule.id)) continue
      if (isVisibilityRule(rule)) {
        if (!visibilitySources(rule).every((source) => fieldsIn.has(source))) continue
        if (!sections.has(rule.doel) && !fieldsIn.has(rule.doel)) continue
      } else {
        const target = 'doelSectie' in rule ? rule.doelSectie : rule.doelVeld
        if (!sections.has(target) && !fieldsIn.has(target)) continue
      }
      out.add(rule.id)
    }
  }

  return { secties: sections, velden: fieldsIn, regels: out }
}

function fieldsOf(definitie: Definitie, sections: ReadonlySet<string>): Set<string> {
  return new Set(
    definitie.secties
      .filter((section) => sections.has(section.id))
      .flatMap((section) => fields(section).map(({ veld: field }) => field.id)),
  )
}

/** Een herhaalgroep zonder rijvelden en een sectie zonder velden bestaan niet in het schema. */
function cleanUp(definitie: Definitie, sections: Set<string>, fieldsIn: Set<string>): void {
  for (const section of definitie.secties) {
    if (!sections.has(section.id)) continue
    for (const field of section.velden) {
      if (field.control !== 'herhaalgroep' || !fieldsIn.has(field.id)) continue
      if (!field.rijVelden.some((rowField) => fieldsIn.has(rowField.id))) fieldsIn.delete(field.id)
    }
    if (!section.velden.some((field) => fieldsIn.has(field.id))) {
      sections.delete(section.id)
      for (const { veld: field } of fields(section)) fieldsIn.delete(field.id)
    }
  }
}

function intersectFields(list: readonly Field[], fieldsIn: ReadonlySet<string>): Field[] {
  const out: Field[] = []
  for (const field of list) {
    if (!fieldsIn.has(field.id)) continue
    if (field.control === 'herhaalgroep') {
      const rowFields = field.rijVelden.filter((rowField) => fieldsIn.has(rowField.id))
      // Leeg kan niet voorkomen: ruimOp heeft zo'n herhaalgroep dan al weggehaald.
      out.push({ ...field, rijVelden: rowFields })
      continue
    }
    out.push(field)
  }
  return out
}
